import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import HoursFilter from "../../components/productsInfosComponents/TroubleshootComponents/HoursFilter";
import { useTroubleshoot } from "../../hooks/useTroubleshoot";
import SystemInfo from "../../components/productsInfosComponents/TroubleshootComponents/SystemInfo";
import ServicesInfo from "../../components/productsInfosComponents/TroubleshootComponents/ServicesInfo";
import { MdSchedule, MdCheckCircle, MdWarning, MdError } from "react-icons/md";
import { toLocalDate } from "../../utils/dateUtils";

const TroubleshootPage = ({ vmIds, vmNames }) => {
  const { getTroubleshootByDateAndVmId, loading, error, troubleshootData } =
    useTroubleshoot();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);
  const [cachedData, setCachedData] = useState({});
  const [forceRefresh, setForceRefresh] = useState(0);
  const [currentVmId, setCurrentVmId] = useState(null);

  const formatDateForApi = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const handleHoursChange = (startDate, endDate, hoursLabel) => {
    setStartDateTime(startDate);
    setEndDateTime(endDate);
    setForceRefresh((prev) => prev + 1);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setStartDateTime(null);
    setEndDateTime(null);
    setForceRefresh((prev) => prev + 1);
    if (vmIds && vmIds.length > 0) {
      const formattedDate = formatDateForApi(date);
      const vmId = vmIds[0];
      setCurrentVmId(vmId);
      getTroubleshootByDateAndVmId(formattedDate, vmId);
    }
  };

  useEffect(() => {
    if (vmIds && vmIds.length > 0) {
      const vmId = vmIds[0];
      setCurrentVmId(vmId);
      const formattedDate = formatDateForApi(selectedDate);
      getTroubleshootByDateAndVmId(formattedDate, vmId);
    }
  }, [vmIds, selectedDate]);

  useEffect(() => {
    if (
      troubleshootData &&
      Array.isArray(troubleshootData) &&
      troubleshootData.length > 0
    ) {
      const dateKey = formatDateForApi(selectedDate);
      const vmKey = currentVmId || (vmIds && vmIds.length > 0 ? vmIds[0] : 'default');
      const cacheKey = `${dateKey}_${vmKey}`;
      setCachedData((prev) => ({
        ...prev,
        [cacheKey]: troubleshootData,
      }));
    }
  }, [troubleshootData, selectedDate, currentVmId, vmIds]);

  const filterDataByTimeRange = (data) => {
    if (!data) return [];

    let dataArray = data;
    if (!Array.isArray(data)) {
      dataArray = [data];
    }

    if (!startDateTime || !endDateTime) {
      return dataArray;
    }

    const start = toLocalDate(startDateTime);
    const end = toLocalDate(endDateTime);

    const filtered = dataArray.filter((item) => {
      const itemDate = toLocalDate(item.stoppedAt || item.createdAt);
      return itemDate >= start && itemDate <= end;
    });

    return filtered;
  };

  const getCurrentData = () => {
    const dateKey = formatDateForApi(selectedDate);
    const vmKey = currentVmId || (vmIds && vmIds.length > 0 ? vmIds[0] : 'default');
    const cacheKey = `${dateKey}_${vmKey}`;
    if (cachedData[cacheKey]) {
      return cachedData[cacheKey];
    }
    return troubleshootData;
  };

  const getLatestSystemData = () => {
    const currentData = getCurrentData();
    if (!currentData) return null;

    if (!isToday(selectedDate)) {
      return null;
    }

    let filteredData = filterDataByTimeRange(currentData);

    if (!filteredData || filteredData.length === 0) return null;

    const sortedByDate = [...filteredData].sort((a, b) => {
      const dateA = toLocalDate(a.stoppedAt || a.createdAt);
      const dateB = toLocalDate(b.stoppedAt || b.createdAt);
      return dateB - dateA;
    });

    const latest = sortedByDate[0];

    if (latest && latest.components && latest.components.system) {
      return latest.components.system;
    }

    return null;
  };

  const getLatestTroubleshootSummary = () => {
    const currentData = getCurrentData();
    if (!currentData) return null;

    let filteredData = filterDataByTimeRange(currentData);

    if (!filteredData || filteredData.length === 0) return null;

    const sortedByDate = [...filteredData].sort((a, b) => {
      const dateA = toLocalDate(a.stoppedAt || a.createdAt);
      const dateB = toLocalDate(b.stoppedAt || b.createdAt);
      return dateB - dateA;
    });

    return sortedByDate[0];
  };

  const currentVmName =
    vmNames && vmNames.length > 0 ? vmNames[0] : "Unknown VM";
  const summaryData = getLatestTroubleshootSummary();
  const latestSystemData = getLatestSystemData();
  const currentData = getCurrentData();
  const filteredData = filterDataByTimeRange(currentData);
  const showSystemInfo = isToday(selectedDate) && latestSystemData !== null;
  const hasData = currentData && Array.isArray(currentData) && currentData.length > 0;

  if (!vmIds || vmIds.length === 0) {
    return (
      <div className="text-center py-12 bg-card-bg rounded-xl border border-border-color">
        <p className="text-primary-dark/60">
          No VM selected. Please select a product from the dropdown above.
        </p>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <HoursFilter
          onHoursChange={handleHoursChange}
          baseDate={selectedDate}
        />
        <div className="bg-primary-cyan rounded-lg p-1">
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            dateFormat="dd/MM/yyyy"
            maxDate={new Date()}
            className="bg-primary-cyan text-white px-3 py-2 rounded-lg focus:outline-none cursor-pointer w-full sm:w-auto text-sm"
          />
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-primary-dark/60">Loading troubleshoot data...</p>
          </div>
        </div>
      )}

      <div className="bg-primary-dark rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-4 sm:p-6 md:p-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div>
              <h3 className="text-white text-lg sm:text-xl md:text-2xl font-bold break-words">
                {currentVmName}
              </h3>
              {showSystemInfo && latestSystemData?.uptime && (
                <div className="flex items-center gap-2 mt-2">
                  <MdSchedule className="text-primary-cyan text-sm sm:text-base" />
                  <span className="text-gray-400 text-xs sm:text-sm">
                    Uptime: {latestSystemData.uptime}
                  </span>
                </div>
              )}
            </div>
            {showSystemInfo && (
              <div className="flex items-center gap-2 bg-primary-cyan/20 px-3 py-1.5 rounded-lg self-start border border-primary-cyan/30">
                {latestSystemData?.status === "OK" && (
                  <MdCheckCircle className="text-green-500 text-lg sm:text-xl" />
                )}
                {latestSystemData?.status === "WARNING" && (
                  <MdWarning className="text-yellow-500 text-lg sm:text-xl" />
                )}
                {latestSystemData?.status !== "OK" &&
                  latestSystemData?.status !== "WARNING" &&
                  latestSystemData?.status && (
                    <MdError className="text-red-500 text-lg sm:text-xl" />
                  )}
                <span
                  className={`font-semibold text-sm sm:text-base ${
                    latestSystemData?.status === "OK"
                      ? "text-green-500"
                      : latestSystemData?.status === "WARNING"
                        ? "text-yellow-500"
                        : "text-red-500"
                  }`}
                >
                  {latestSystemData?.status || "UNKNOWN"}
                </span>
              </div>
            )}
          </div>
        </div>

        {!hasData && !loading && (
          <div className="p-4 sm:p-6 md:p-8 pt-0">
            <div className="bg-gray-800/30 rounded-xl border border-gray-700 p-8 text-center">
              <p className="text-gray-400">
                No troubleshoot data for this VM
              </p>
            </div>
          </div>
        )}

        {hasData && showSystemInfo && <SystemInfo systemData={latestSystemData} />}

        {hasData && (
          <ServicesInfo
            key={forceRefresh + currentVmId}
            servicesData={filteredData}
            startDateTime={startDateTime}
            endDateTime={endDateTime}
            baseDate={selectedDate}
          />
        )}
      </div>
    </div>
  );
};

export default TroubleshootPage;